import { useScrollReveal } from '../hooks/useScrollReveal';
import { useLang } from '../i18n/LanguageContext';
import styles from './Faq.module.css';

// Keep in sync with the FAQPage JSON-LD (index.html for EN, Seo.jsx DE_JSON_LD for DE).
const FAQS = [
  {
    q: {
      en: 'Where are you based, and do you travel?',
      de: 'Wo sitzen Sie und reisen Sie auch an?',
    },
    a: {
      en: "We're based in Berlin and travel constantly. We've filmed events in 18 countries across Europe and beyond. If your event has a location, we'll be there.",
      de: 'Wir sitzen in Berlin und sind ständig unterwegs. Wir haben Events in 18 Ländern in ganz Europa und darüber hinaus gefilmt. Wenn Ihr Event einen Ort hat, sind wir da.',
    },
  },
  {
    q: { en: 'How fast can you deliver?', de: 'Wie schnell können Sie liefern?' },
    a: {
      en: 'Standard delivery is within days of the event. If you need faster, we offer on-location editing with 24-hour turnaround: finished clips delivered while the event is still running.',
      de: 'Standard-Lieferung erfolgt innerhalb weniger Tage nach dem Event. Wenn es schneller sein muss, bieten wir Vor-Ort-Schnitt mit 24-Stunden-Zusage: fertige Clips, geliefert während das Event noch läuft.',
    },
  },
  {
    q: { en: 'What types of events do you cover?', de: 'Welche Arten von Events begleiten Sie?' },
    a: {
      en: 'Multi-day conferences, medical congresses, industry summits, exhibitions and brand events: anywhere from a single stage to a whole venue.',
      de: 'Mehrtägige Konferenzen, medizinische Kongresse, Branchen-Summits, Messen und Marken-Events — von einer einzelnen Bühne bis zu einer ganzen Location.',
    },
  },
  {
    q: { en: 'What kinds of videos do you make?', de: 'Welche Arten von Videos produzieren Sie?' },
    a: {
      en: 'Event highlight films, daily recap clips, keynote and session coverage, speaker interviews, fireside chats and podcast recordings, booth videos and social-media cuts, plus brand films built from event footage.',
      de: 'Event-Highlight-Filme, tägliche Recap-Clips, Keynote- und Session-Coverage, Speaker-Interviews, Fireside Chats und Podcast-Aufnahmen, Stand-Videos und Social-Media-Schnitte — sowie Markenfilme aus Event-Footage.',
    },
  },
  {
    q: { en: 'Do you only cover large events?', de: 'Begleiten Sie nur große Events?' },
    a: {
      en: "Not at all. We scale the crew to the event. Small events and brand shoots often need just one cinematographer who shoots and edits; that\u2019s how we filmed RavenDB and Waves Audio. Same standard, fewer people.",
      de: 'Überhaupt nicht. Wir skalieren das Team nach dem Event. Kleine Events und Marken-Drehs brauchen oft nur eine:n Kameramann:in, die/der filmt und schneidet; so haben wir RavenDB und Waves Audio gefilmt. Gleicher Standard, weniger Personen.',
    },
  },
  {
    q: { en: 'Do you bring your own crew and gear?', de: 'Bringen Sie eigenes Team und eigenes Equipment mit?' },
    a: {
      en: 'Yes, we arrive fully equipped: camera, sound, lighting and an edit setup. Crew size depends on the brief; most projects need one or two people, and when wider coverage is needed we bring in trusted colleagues we\u2019ve worked with for years.',
      de: 'Ja, wir kommen vollständig ausgestattet: Kamera, Ton, Licht und ein Schnitt-Setup. Die Teamgröße hängt vom Briefing ab; die meisten Projekte brauchen ein oder zwei Personen, und wenn breitere Coverage nötig ist, holen wir vertraute Kolleg:innen dazu, mit denen wir schon jahrelang arbeiten.',
    },
  },
  {
    q: { en: 'Do you also do photography?', de: 'Machen Sie auch Fotografie?' },
    a: {
      en: "Photography isn't our specialty. We'd rather tell you that than deliver something average. But after 20+ years in this business we know the best event photographers around, and we're happy to connect you with the right person for your event.",
      de: 'Fotografie ist nicht unsere Spezialität. Wir sagen Ihnen das lieber, als etwas Durchschnittliches zu liefern. Aber nach über 20 Jahren in der Branche kennen wir die besten Event-Fotograf:innen und verbinden Sie gerne mit der richtigen Person für Ihr Event.',
    },
  },
  {
    q: { en: 'How early should we book?', de: 'Wie früh sollten wir buchen?' },
    a: {
      en: "Ideally 4–6 weeks before your event so we can plan coverage properly. That said, we've jumped in days before an event. Ask, and we'll tell you honestly if we can make it work.",
      de: 'Idealerweise 4–6 Wochen vor Ihrem Event, damit wir die Coverage richtig planen können. Trotzdem sind wir schon Tage vor einem Event eingesprungen. Fragen Sie — und wir sagen Ihnen ehrlich, ob wir es schaffen.',
    },
  },
];

export default function Faq() {
  const revealRef = useScrollReveal();
  const { t, lang } = useLang();

  return (
    <section id="faq" className={styles.section}>
      <div className={`container reveal ${styles.inner}`} ref={revealRef}>
        <p className="section-label">{t('faq.eyebrow')}</p>
        <h2 className={styles.headline}>{t('faq.eyebrow') === 'Good to Know' ? 'Questions we hear a lot.' : 'Fragen, die wir oft hören.'}</h2>

        <div className={styles.list}>
          {FAQS.map((item) => (
            <details key={item.q.en} className={styles.item}>
              <summary className={styles.question}>
                {item.q[lang] ?? item.q.en}
                <span className={styles.marker} aria-hidden="true" />
              </summary>
              <p className={styles.answer}>{item.a[lang] ?? item.a.en}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}
